"use client";
import { useState } from "react";

import PageShell from "@/components/PageShell";
type FormState = {
  name: string;
  email: string;
  phone: string;
  address: string;
  company: string;
  teamSize: string;
  role: string;
  preferredTime: string;
  message: string;
};

type Status = "idle" | "loading" | "success" | "error";

const INITIAL: FormState = {
  name: "",
  email: "",
  phone: "",
  address: "",
  company: "",
  teamSize: "",
  role: "",
  preferredTime: "",
  message: "",
};

const TEAM_SIZES = [
  "Just me",
  "2–10",
  "11–50",
  "51–200",
  "201–1000",
  "1000+",
];

const ROLES = [
  "Founder / Owner",
  "General Counsel / Legal Head",
  "Practising Lawyer",
  "Finance / Accounting",
  "Compliance / Risk",
  "Procurement / Operations",
  "Other",
];

const TIMES = [
  "Morning (10am–12pm)",
  "Afternoon (12pm–4pm)",
  "Evening (4pm–7pm)",
  "Flexible — propose times",
];

export default function BookDemoPage() {
  const [form, setForm] = useState<FormState>(INITIAL);
  const [errors, setErrors] = useState<Partial<FormState>>({});
  const [status, setStatus] = useState<Status>("idle");

  function validate(): boolean {
    const e: Partial<FormState> = {};
    if (!form.name.trim()) e.name = "Name is required";
    if (!form.email.trim()) e.email = "Work email is required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "Enter a valid email";
    if (!form.company.trim()) e.company = "Company is required";
    if (form.phone.trim() && !/^[+\d][\d\s()-]{6,19}$/.test(form.phone.trim()))
      e.phone = "Enter a valid mobile number";
    if (!form.teamSize) e.teamSize = "Pick a team size";
    if (!form.role) e.role = "Pick a role";
    if (!form.preferredTime) e.preferredTime = "Pick a preferred time";
    setErrors(e);
    return Object.keys(e).length === 0;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!validate()) return;
    setStatus("loading");
    try {
      const res = await fetch("/api/book-demo", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: form.name,
          email: form.email,
          phone: form.phone,
          address: form.address,
          company: form.company,
          teamSize: form.teamSize,
          role: form.role,
          preferredTime: form.preferredTime,
          message: form.message,
        }),
      });
      if (!res.ok) throw new Error("Server error");
      setStatus("success");
      setForm(INITIAL);
      setErrors({});
    } catch {
      setStatus("error");
    }
  }

  function handleChange(field: keyof FormState) {
    return (
      e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>,
    ) => {
      setForm(prev => ({ ...prev, [field]: e.target.value }));
      if (errors[field]) setErrors(prev => ({ ...prev, [field]: undefined }));
    };
  }

  const inputCls = (k: keyof FormState, type: "input" | "select" | "textarea") => {
    const base = type === "select" ? "bd-select" : type === "textarea" ? "bd-textarea" : "bd-input";
    return errors[k] ? `${base} bd-error` : base;
  };

  return (
    <>
      <PageShell>
        {/* Hero */}
        <section
          style={{
            padding: "72px clamp(20px,4vw,52px) 56px",
            borderBottom: "1px solid var(--b200)",
            background: "var(--b50)",
          }}
        >
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1.15fr 1fr",
              gap: "52px",
              alignItems: "center",
            }}
          >
            <div>
              <div className="ey rev">Book a demo</div>
              <h1
                className="sh2 rev d1"
                style={{ fontSize: "clamp(36px,4.5vw,58px)" }}
              >
                See Lex<span className="vio-mark">Vio</span> in your stack.{" "}<br />
                30 minutes. <em>Your contracts.</em>
              </h1>
              <p
                className="rev d2"
                style={{
                  fontSize: "16px",
                  color: "rgba(19,18,16,.55)",
                  lineHeight: 1.75,
                  maxWidth: "520px",
                  marginTop: "18px",
                }}
              >
                Walk through <span className="vio-mark">Vio</span> and Nexus with a member of our team —
                using your own document. We&apos;ll scan a real contract, show the
                risk report, run a cross-portfolio search, and answer every
                question. No slides, no canned demo.
              </p>
            </div>

            {/* Scheduler preview card */}
            <div
              className="rev d2"
              style={{
                background: "#fff",
                border: "1px solid var(--b200)",
                borderRadius: "8px",
                padding: "22px",
                boxShadow: "0 1px 0 rgba(19,18,16,.02),0 14px 36px rgba(19,18,16,.05)",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: "8px",
                  marginBottom: "14px",
                  paddingBottom: "12px",
                  borderBottom: "1px solid var(--b200)",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <svg aria-hidden="true" fill="none" height="14" stroke="currentColor" strokeWidth="1.4" style={{ color: "var(--cu)" }} viewBox="0 0 14 14" width="14">
                    <circle cx="7" cy="7" r="5.5" />
                    <polyline points="7,3.5 7,7 9.5,8.5" />
                  </svg>
                  <span
                    style={{
                      fontFamily: "var(--font-mono), ui-monospace, monospace",
                      fontSize: "10px",
                      letterSpacing: ".16em",
                      textTransform: "uppercase",
                      color: "var(--o900)",
                    }}
                  >
                    30-min walkthrough
                  </span>
                </div>
              </div>

              <div
                style={{
                  fontSize: "11.5px",
                  color: "rgba(19,18,16,.5)",
                  marginBottom: "10px",
                  letterSpacing: ".02em",
                }}
              >
                This week — next available
              </div>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: "8px",
                }}
              >
                {[
                  { d: "Mon", t: "11:00 – 11:30" },
                  { d: "Tue", t: "14:30 – 15:00" },
                  { d: "Wed", t: "16:00 – 16:30" },
                  { d: "Thu", t: "10:30 – 11:00" },
                ].map(slot => (
                  <div
                    key={slot.d}
                    style={{
                      padding: "10px 12px",
                      background: "var(--b50)",
                      border: "1px solid var(--b200)",
                      borderLeft: "2px solid var(--cu)",
                      borderRadius: "4px",
                    }}
                  >
                    <div
                      style={{
                        fontFamily: "var(--font-mono), ui-monospace, monospace",
                        fontSize: "9.5px",
                        letterSpacing: ".14em",
                        textTransform: "uppercase",
                        color: "rgba(19,18,16,.5)",
                      }}
                    >
                      {slot.d}
                    </div>
                    <div
                      style={{
                        fontFamily: "var(--font-fraunces), serif",
                        fontSize: "15px",
                        fontWeight: 300,
                        color: "var(--o900)",
                        letterSpacing: "-.01em",
                        marginTop: "2px",
                      }}
                    >
                      {slot.t}
                    </div>
                    <div
                      style={{
                        fontSize: "10px",
                        color: "rgba(22,101,52,.85)",
                        marginTop: "4px",
                        display: "flex",
                        alignItems: "center",
                        gap: "5px",
                      }}
                    >
                      <span
                        aria-hidden="true"
                        style={{
                          width: "5px",
                          height: "5px",
                          borderRadius: "50%",
                          background: "var(--green)",
                          display: "inline-block",
                        }}
                      ></span>
                      Available
                    </div>
                  </div>
                ))}
              </div>

              <div
                style={{
                  marginTop: "14px",
                  paddingTop: "12px",
                  borderTop: "1px solid var(--b200)",
                  fontSize: "11.5px",
                  color: "rgba(19,18,16,.55)",
                  lineHeight: 1.55,
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: "10px",
                }}
              >
                <span>
                  Hosted by founders · we&apos;ll send a recording if you&apos;d like one
                </span>
                <span
                  style={{
                    fontFamily: "var(--font-mono), ui-monospace, monospace",
                    fontSize: "9.5px",
                    letterSpacing: ".12em",
                    color: "var(--cu)",
                  }}
                >
                  &lt; 4h reply
                </span>
              </div>
            </div>
          </div>
        </section>

        {/* Form + sidebar */}
        <section
          style={{
            padding: "56px clamp(20px,4vw,52px) 72px",
            borderBottom: "1px solid var(--b200)",
          }}
        >
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1.4fr 1fr",
              gap: "56px",
              alignItems: "start",
            }}
          >
            {/* Form */}
            <form noValidate onSubmit={handleSubmit}>
              <h2
                style={{
                  fontFamily: "var(--font-fraunces), serif",
                  fontSize: "22px",
                  fontWeight: 200,
                  color: "var(--o900)",
                  marginBottom: "22px",
                }}
              >
                Tell us a little about you
              </h2>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" }}>
                <div>
                  <label className="bd-label" htmlFor="bd-name">Full name</label>
                  <input
                    autoComplete="name"
                    className={inputCls("name", "input")}
                    id="bd-name"
                    placeholder="Jordan Lee"
                    type="text"
                    value={form.name}
                    onChange={handleChange("name")}
                  />
                  {errors.name && <div className="bd-errtext">{errors.name}</div>}
                </div>
                <div>
                  <label className="bd-label" htmlFor="bd-email">Work email</label>
                  <input
                    autoComplete="email"
                    className={inputCls("email", "input")}
                    id="bd-email"
                    placeholder="jordan@yourcompany.com"
                    type="email"
                    value={form.email}
                    onChange={handleChange("email")}
                  />
                  {errors.email && <div className="bd-errtext">{errors.email}</div>}
                </div>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px", marginTop: "16px" }}>
                <div>
                  <label className="bd-label" htmlFor="bd-company">Company</label>
                  <input
                    autoComplete="organization"
                    className={inputCls("company", "input")}
                    id="bd-company"
                    placeholder="Your firm or company"
                    type="text"
                    value={form.company}
                    onChange={handleChange("company")}
                  />
                  {errors.company && <div className="bd-errtext">{errors.company}</div>}
                </div>
                <div>
                  <label className="bd-label" htmlFor="bd-phone">
                    Mobile number<span className="bd-label-opt">optional</span>
                  </label>
                  <input
                    autoComplete="tel"
                    className={inputCls("phone", "input")}
                    id="bd-phone"
                    inputMode="tel"
                    placeholder="+1 555 123 4567"
                    type="tel"
                    value={form.phone}
                    onChange={handleChange("phone")}
                  />
                  {errors.phone && <div className="bd-errtext">{errors.phone}</div>}
                </div>
              </div>

              <div style={{ marginTop: "16px" }}>
                <label className="bd-label" htmlFor="bd-address">
                  Address / city<span className="bd-label-opt">optional</span>
                </label>
                <input
                  autoComplete="street-address"
                  className={inputCls("address", "input")}
                  id="bd-address"
                  placeholder="City, state — or full office address"
                  type="text"
                  value={form.address}
                  onChange={handleChange("address")}
                />
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px", marginTop: "16px" }}>
                <div>
                  <label className="bd-label" htmlFor="bd-team">Team size</label>
                  <select
                    className={inputCls("teamSize", "select")}
                    id="bd-team"
                    value={form.teamSize}
                    onChange={handleChange("teamSize")}
                  >
                    <option value="">Select team size…</option>
                    {TEAM_SIZES.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                  {errors.teamSize && <div className="bd-errtext">{errors.teamSize}</div>}
                </div>
                <div>
                  <label className="bd-label" htmlFor="bd-role">Your role</label>
                  <select
                    className={inputCls("role", "select")}
                    id="bd-role"
                    value={form.role}
                    onChange={handleChange("role")}
                  >
                    <option value="">Select role…</option>
                    {ROLES.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                  {errors.role && <div className="bd-errtext">{errors.role}</div>}
                </div>
              </div>

              <div style={{ marginTop: "16px" }}>
                <label className="bd-label" htmlFor="bd-time">Preferred time</label>
                <select
                  className={inputCls("preferredTime", "select")}
                  id="bd-time"
                  value={form.preferredTime}
                  onChange={handleChange("preferredTime")}
                >
                  <option value="">Select preferred time…</option>
                  {TIMES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
                {errors.preferredTime && <div className="bd-errtext">{errors.preferredTime}</div>}
              </div>

              <div style={{ marginTop: "16px" }}>
                <label className="bd-label" htmlFor="bd-message">
                  Anything we should know?<span className="bd-label-opt">optional</span>
                </label>
                <textarea
                  className={inputCls("message", "textarea")}
                  id="bd-message"
                  placeholder="e.g. We mostly work on vendor MSAs and want to see the Nexus search."
                  rows={4}
                  value={form.message}
                  onChange={handleChange("message")}
                />
              </div>

              <div style={{ marginTop: "20px", display: "flex", gap: "14px", alignItems: "center", flexWrap: "wrap" }}>
                <button
                  className="cta-p"
                  disabled={status === "loading"}
                  style={{ opacity: status === "loading" ? 0.6 : 1, cursor: status === "loading" ? "wait" : "pointer" }}
                  type="submit"
                >
                  <span className="cta-p-t">
                    {status === "loading" ? "Sending…" : "Request demo →"}
                  </span>
                </button>
                <span style={{ fontSize: "12px", color: "rgba(19,18,16,.5)" }}>
                  We&apos;ll reply within one business day.
                </span>
              </div>

              {status === "success" && (
                <div
                  role="status"
                  style={{
                    marginTop: "20px",
                    padding: "14px 16px",
                    background: "rgba(22,163,74,.08)",
                    border: "1px solid rgba(22,163,74,.22)",
                    borderRadius: "4px",
                    fontSize: "14px",
                    color: "rgba(22,101,52,.95)",
                  }}
                >
                  ✓ Request received. We&apos;ll be in touch within a business day to confirm a time.
                </div>
              )}
              {status === "error" && (
                <div
                  role="alert"
                  style={{
                    marginTop: "20px",
                    padding: "14px 16px",
                    background: "rgba(220,38,38,.08)",
                    border: "1px solid rgba(220,38,38,.22)",
                    borderRadius: "4px",
                    fontSize: "14px",
                    color: "rgba(127,29,29,.95)",
                  }}
                >
                  Something went wrong. Please try again, or email{" "}
                  <a href="mailto:support@globalsynapsetech.com" style={{ color: "var(--cu)" }}>support@globalsynapsetech.com</a>.
                </div>
              )}
            </form>

            {/* Sidebar — what you'll see */}
            <aside
              style={{
                background: "var(--b100)",
                border: "1px solid var(--b200)",
                borderRadius: "6px",
                padding: "28px",
                position: "sticky",
                top: "92px",
              }}
            >
              <div
                style={{
                  fontFamily: "var(--font-mono), ui-monospace, monospace",
                  fontSize: "10px",
                  letterSpacing: ".16em",
                  textTransform: "uppercase",
                  color: "var(--cu)",
                  marginBottom: "12px",
                }}
              >
                What you&apos;ll see in 30 minutes
              </div>
              <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: "12px" }}>
                {[
                  ["Live contract review", "We scan a contract you bring — clauses flagged, risks scored, fixes proposed in seconds."],
                  // "grounded in statute and case law" dropped 2026-09-05:
                  // LexVio holds no case-law or statute corpus for the
                  // jurisdictions this product serves, and says so on every
                  // citation it cannot resolve.
                  ["Vio in action", "Highlight a clause, ask a question, get an answer cited to the document — with a verdict on every citation."],
                  ["Nexus across your vault", "Search a query like “what's our standard liability cap?” across multiple uploaded contracts."],
                  ["Pricing tailored to you", "We'll discuss the plan that fits your team — solo, team, business, or enterprise."],
                ].map(([t, d], i) => (
                  <li key={i} style={{ display: "flex", gap: "10px", alignItems: "flex-start" }}>
                    <span style={{ color: "var(--cu)", flexShrink: 0, marginTop: "2px" }}>✓</span>
                    <div>
                      <div style={{ fontSize: "13.5px", color: "var(--o900)", fontWeight: 500, marginBottom: "3px" }}>{t}</div>
                      <div style={{ fontSize: "12.5px", color: "rgba(19,18,16,.55)", lineHeight: 1.6 }}>{d}</div>
                    </div>
                  </li>
                ))}
              </ul>

              <div
                style={{
                  marginTop: "24px",
                  paddingTop: "20px",
                  borderTop: "1px solid var(--b200)",
                  fontSize: "12px",
                  color: "rgba(19,18,16,.5)",
                  lineHeight: 1.65,
                }}
              >
                Prefer to try it yourself first?{" "}
                <a href="/scan" style={{ color: "var(--cu)" }}>Run a free public scan →</a>
              </div>
            </aside>
          </div>
        </section>
      </PageShell>
    </>
  );
}
